/* ============================================================
   Aplanta Produktberater – Wizard Logic
   ============================================================

   Produkt-Tag-Schema:
   Pflanzen im Shopify Admin müssen folgende Tags erhalten:

   Raum:    advisor-room:wohnzimmer | advisor-room:buero | advisor-room:schlafzimmer
            advisor-room:flur | advisor-room:outdoor | advisor-room:gewerbe
   Wirkung: advisor-impact:statement | advisor-impact:akzent
            advisor-impact:wand | advisor-impact:haengend
   Stil:    advisor-style:modern | advisor-style:tropisch
            advisor-style:boho | advisor-style:klassisch

   Budget wird per Preis gefiltert (kein Tag nötig).
   ============================================================ */

(function () {
  'use strict';

  const TOTAL_STEPS = 4;

  const state = {
    step: 1,
    answers: {
      room: null,
      impact: null,
      style: null,
      budget: null,
    },
    products: null,
    loading: false,
  };

  // ---- DOM refs ----
  let wizard, stepsEl, resultsEl, progressBar, stepCounter, backBtn, restartBtn;

  function init() {
    wizard     = document.getElementById('produktberater');
    if (!wizard) return;

    stepsEl    = wizard.querySelector('.pb-steps');
    resultsEl  = document.getElementById('pb-results');
    progressBar = document.getElementById('pb-progress-bar');
    stepCounter = document.getElementById('pb-step-counter');
    backBtn     = document.getElementById('pb-back');
    restartBtn  = document.getElementById('pb-restart');

    // Card clicks
    wizard.addEventListener('click', onCardClick);
    backBtn.addEventListener('click', goBack);
    restartBtn.addEventListener('click', restart);

    // Prefetch products immediately in background
    fetchProducts();

    render();
  }

  // ---- Navigation ----

  function goBack() {
    if (state.step <= 1) return;
    state.step--;

    // Clear the answer for the step we're returning to
    const keys = ['room', 'impact', 'style', 'budget'];
    state.answers[keys[state.step - 1]] = null;

    resultsEl.hidden = true;
    render();
  }

  function restart() {
    state.step = 1;
    state.answers = { room: null, impact: null, style: null, budget: null };
    resultsEl.hidden = true;
    render();
  }

  // ---- Card click handler ----

  function onCardClick(e) {
    const card = e.target.closest('.pb-card[data-key]');
    if (!card || state.loading) return;

    const key   = card.dataset.key;
    const value = card.dataset.value;

    // Visual: deselect siblings, select clicked
    card.closest('.pb-cards').querySelectorAll('.pb-card').forEach(c => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    // Save answer
    state.answers[key] = value;

    // Short delay so the selection is visible before advancing
    setTimeout(() => {
      if (state.step < TOTAL_STEPS) {
        state.step++;
        render();
      } else {
        showResults();
      }
    }, 220);
  }

  // ---- Render wizard ----

  function render() {
    // Show/hide steps
    stepsEl.querySelectorAll('.pb-step').forEach(el => {
      const stepNum = parseInt(el.dataset.step, 10);
      el.hidden = (stepNum !== state.step);
    });

    // Restore previously selected card if user went back
    const keys = ['room', 'impact', 'style', 'budget'];
    const currentKey = keys[state.step - 1];
    const currentAnswer = state.answers[currentKey];

    const currentStep = stepsEl.querySelector(`.pb-step[data-step="${state.step}"]`);
    if (currentStep && currentAnswer) {
      currentStep.querySelectorAll('.pb-card').forEach(c => {
        c.classList.toggle('is-selected', c.dataset.value === currentAnswer);
      });
    }

    // Progress bar
    progressBar.style.width = ((state.step - 1) / TOTAL_STEPS * 100) + '%';

    // Step counter
    stepCounter.textContent = `Schritt ${state.step} von ${TOTAL_STEPS}`;

    // Back button
    backBtn.style.display = state.step > 1 ? '' : 'none';

    // Show steps, hide results
    stepsEl.style.display = '';
  }

  // ---- Results ----

  function showResults() {
    stepsEl.style.display = 'none';
    backBtn.style.display = 'none';
    stepCounter.textContent = '';
    progressBar.style.width = '100%';

    resultsEl.hidden = false;
    resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

    if (state.products) {
      renderResults(state.products);
    } else {
      // Show loading state and wait for fetch
      const grid = document.getElementById('pb-results-grid');
      grid.innerHTML = '<div class="pb-loading"><div class="pb-loading__spinner"></div><p>Passende Pflanzen werden gesucht…</p></div>';
      state.loading = true;

      fetchProducts().then(products => {
        state.loading = false;
        renderResults(products);
      });
    }
  }

  function renderResults(products) {
    const grid     = document.getElementById('pb-results-grid');
    const subtitle = document.getElementById('pb-results-subtitle');
    const matched  = scoreAndFilter(products);

    const styleLabels = {
      modern:   'modern & minimalistisch',
      tropisch: 'tropisch & exotisch',
      boho:     'boho & natürlich',
      klassisch:'klassisch & zeitlos',
    };
    const roomLabels = {
      wohnzimmer: 'dein Wohnzimmer',
      buero:      'dein Büro',
      schlafzimmer:'dein Schlafzimmer',
      flur:       'deinen Flur',
      outdoor:    'deinen Außenbereich',
      gewerbe:    'deinen Gewerbebetrieb',
    };

    const room  = state.answers.room;
    const style = state.answers.style;
    subtitle.textContent = matched.length > 0
      ? `${matched.length} Empfehlungen für ${roomLabels[room] || 'dich'} im Stil ${styleLabels[style] || ''}.`
      : '';

    if (matched.length === 0) {
      grid.innerHTML = `
        <div class="pb-no-results">
          <p>Für deine Auswahl haben wir leider keine direkten Treffer gefunden.<br>Schau gerne in unserem gesamten Sortiment vorbei.</p>
          <a href="/collections/all">Alle Produkte ansehen →</a>
        </div>`;
      return;
    }

    grid.innerHTML = matched.map((product, i) => productCard(product, i === 0)).join('');
  }

  function productCard(product, isTop) {
    const price = formatPrice(product.price_min || product.variants?.[0]?.price || 0);
    const image = product.images?.[0]?.src || product.featured_image || '';
    const url   = product.url || `/products/${product.handle}`;
    const title = escapeHtml(product.title);

    const imgTag = image
      ? `<img src="${image}" alt="${title}" loading="lazy" width="400" height="400">`
      : `<div style="width:100%;aspect-ratio:1;background:#e8e8e3;"></div>`;

    return `
      <div class="pb-product${isTop ? ' pb-product--top' : ''}">
        <span class="pb-product__badge">Beste Wahl</span>
        <div class="pb-product__image-wrap">${imgTag}</div>
        <div class="pb-product__body">
          <p class="pb-product__title">${title}</p>
          <p class="pb-product__price">${price}</p>
          <a href="${url}" class="pb-product__cta">Zum Produkt</a>
        </div>
      </div>`;
  }

  // ---- Scoring ----

  function scoreAndFilter(products) {
    const { room, impact, style, budget } = state.answers;
    const [budgetMin, budgetMax] = parseBudget(budget);

    const scored = products
      .map(product => {
        const tags = (product.tags || []).map(t => t.toLowerCase().trim());
        const priceInCents = product.price_min || product.variants?.[0]?.price || 0;
        const priceEur = priceInCents / 100;

        // Price filter – hard exclude
        if (priceEur < budgetMin || priceEur > budgetMax) return null;

        let score = 0;

        // Room match (2 pts)
        if (room && tags.includes(`advisor-room:${room}`)) score += 2;

        // Impact match (2 pts)
        if (impact && tags.includes(`advisor-impact:${impact}`)) score += 2;

        // Style match (3 pts – most differentiating)
        if (style && tags.includes(`advisor-style:${style}`)) score += 3;

        // Bonus: all three match
        if (score >= 7) score += 1;

        return { product, score };
      })
      .filter(Boolean)
      .filter(item => item.score > 0);

    // Sort by score desc, then by price asc as tiebreaker
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const pa = a.product.price_min || a.product.variants?.[0]?.price || 0;
      const pb = b.product.price_min || b.product.variants?.[0]?.price || 0;
      return pa - pb;
    });

    // If fewer than 4 results with score > 0, relax: show products matching at least style
    let results = scored.slice(0, 4).map(s => s.product);

    if (results.length < 2) {
      // Fallback: style match only, within budget
      const fallback = products
        .filter(p => {
          const tags = (p.tags || []).map(t => t.toLowerCase().trim());
          const price = (p.price_min || p.variants?.[0]?.price || 0) / 100;
          return price >= budgetMin && price <= budgetMax && style && tags.includes(`advisor-style:${style}`);
        })
        .slice(0, 4);

      if (fallback.length > results.length) results = fallback;
    }

    return results;
  }

  // ---- Product fetching ----

  let fetchPromise = null;

  function fetchProducts() {
    if (fetchPromise) return fetchPromise;

    fetchPromise = (async () => {
      try {
        const allProducts = [];
        let page = 1;
        const limit = 250;

        while (true) {
          const res = await fetch(`/collections/all/products.json?limit=${limit}&page=${page}`);
          if (!res.ok) break;
          const data = await res.json();
          if (!data.products || data.products.length === 0) break;
          allProducts.push(...data.products);
          if (data.products.length < limit) break;
          page++;
        }

        state.products = allProducts;
        return allProducts;
      } catch (err) {
        console.error('[Produktberater] Fehler beim Laden der Produkte:', err);
        state.products = [];
        return [];
      }
    })();

    return fetchPromise;
  }

  // ---- Helpers ----

  function parseBudget(value) {
    if (!value) return [0, 99999];
    const [min, max] = value.split('-').map(Number);
    return [min, max];
  }

  function formatPrice(cents) {
    const eur = cents / 100;
    return eur.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ---- Boot ----

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
